<script lang="ts">
export default {
  name: 'ColorPickerMenu',
};
</script>

<script setup lang="ts">
import { ref, watch, computed } from 'vue';
import ColorSwatch from '@/views/tower/v2/ColorSwatch.vue';
import { Commit } from '@/store';
import { StoreMutationType } from '@/types/StoreMutationType';
import { Check, Edit2, Plus } from 'lucide-vue-next';

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

interface ColorSwatchItem {
  type: 'default' | 'custom' | 'new';
  color: string;
  selected?: boolean;
}

const props = defineProps<{
  colors: string[];
  color?: string;
  value?: string;
}>();

const menu = ref<boolean>(false);
const showColorPickerMenu = ref<boolean>(false);
const editMode = ref<boolean>(false);
const colorPickerSelection = ref<string>('');
const previousColor = ref<string>('');
const colorSwatches = ref<ColorSwatchItem[]>([]);
const currentSwatch = ref<ColorSwatchItem>();

watch(
  () => props.color,
  (newValue) => {
    updateSelectedColor(newValue ?? '');
  },
);

// this will close the color picker if it's open;
// otherwise it will still be open when the custom colors menu is opened
watch(menu, (newValue) => {
  if (!newValue) {
    showColorPickerMenu.value = false;
    editMode.value = false;
  } else {
    setColorsFromLocalStorage();
  }
});

watch(showColorPickerMenu, (isColorPickerOpen) => {
  Commit(StoreMutationType.SET_IS_EDITING, isColorPickerOpen);
});

const customColors = computed(() => {
  return colorSwatches.value.filter((swatch) => swatch.type === 'custom');
});

function setColorsFromLocalStorage() {
  const customColorData = localStorage.getItem('customColorMemory');
  const storedCustomColors = customColorData ? JSON.parse(customColorData) : [];
  colorSwatches.value = props.colors.map((color) => ({ type: 'default', color }));

  if (
    Array.isArray(storedCustomColors) &&
    storedCustomColors.length > 0 &&
    storedCustomColors[0].type &&
    storedCustomColors[0].color
  ) {
    colorSwatches.value = [...colorSwatches.value, ...storedCustomColors];
  } else {
    localStorage.setItem('customColorMemory', '[]');
  }

  updateSelectedColor(props.color ?? '');
}

function updateSelectedColor(color: string) {
  colorSwatches.value = colorSwatches.value.map((s) => {
    s.selected = s.color === color;
    return s;
  });
}

function selectColor(swatch: ColorSwatchItem): void {
  // clear all selected
  colorSwatches.value = colorSwatches.value.map((s) => {
    s.selected = s.color === swatch.color && s.type === swatch.type;
    return s;
  });
}

function handleCancel(): void {
  showColorPickerMenu.value = false;
}

function handleSave(): void {
  const customSwatches: ColorSwatchItem[] = colorSwatches.value.filter((s) => s.type === 'custom');
  const index = customSwatches.findIndex((s) => s.color === currentSwatch.value?.color);

  // check if the color picker selection is a duplicate, and return if so
  if (customSwatches.some((s) => s.color === colorPickerSelection.value)) {
    return;
  }

  const newSwatch: ColorSwatchItem = {
    type: 'custom',
    color: colorPickerSelection.value,
  };

  if (currentSwatch.value?.type != 'new') {
    customSwatches[index] = newSwatch;
  } else {
    customSwatches.push(newSwatch);
  }

  const defaultSwatches = colorSwatches.value.filter((s) => {
    return s.type === 'default';
  });

  colorSwatches.value = [...defaultSwatches, ...customSwatches];

  selectColor(newSwatch);

  localStorage.setItem('customColorMemory', JSON.stringify(colorSwatches.value.filter((s) => s.type === 'custom')));
  // emit back to v-model to send the selected color up to any higher listeners
  emit('update:modelValue', colorPickerSelection.value);
  showColorPickerMenu.value = false;
  editMode.value = false;
}

function handleSwatchClick(swatch: ColorSwatchItem): void {
  currentSwatch.value = swatch;
  if (editMode.value) {
    colorPickerSelection.value = swatch.color;
    previousColor.value = swatch.color;
    showColorPickerMenu.value = true;
  } else {
    selectColor(swatch);
    emit('update:modelValue', swatch.color);
  }
}

function handleSwatchRemove(swatch: ColorSwatchItem): void {
  colorSwatches.value = colorSwatches.value.filter((s) => s.color !== swatch.color || s.type !== swatch.type);

  if (swatch.selected) {
    const defaultSwatch = colorSwatches.value.filter((s) => s.type === 'default')[0];
    selectColor(defaultSwatch);
    colorPickerSelection.value = defaultSwatch.color;
    previousColor.value = defaultSwatch.color;
    emit('update:modelValue', colorPickerSelection.value);
  }

  localStorage.setItem('customColorMemory', JSON.stringify(colorSwatches.value.filter((s) => s.type === 'custom')));
}

function handleAddCustomColor(): void {
  // reset color picker selection so it always starts the same for new swatches
  editMode.value = false;
  colorPickerSelection.value = '#FF0000FF';
  currentSwatch.value = { type: 'new', color: '#FF0000FF' };
  showColorPickerMenu.value = true;
}

function toggleEditCustomColors(): void {
  editMode.value = !editMode.value;
}
</script>

<template>
  <div class="text-center">
    <v-menu
      content-class="tw:border tw:border-solid tw:border-tower-border tw:rounded-lg tw:shadow-lg tw:overflow-y-hidden"
      v-model="menu"
      :close-on-content-click="false"
      :transition="false"
      min-width="auto"
      max-width="200"
      location="end">
      <template v-slot:activator="{ props: vProps }">
        <div
          class="tw:flex tw:h-[30px] tw:w-[30px] tw:items-center tw:justify-center tw:rounded tw:border tw:border-solid tw:border-transparent tw:p-0 tw:hover:border-gray-400">
          <v-btn
            class="tw:h-[26px] tw:w-[26px] tw:min-w-[26px] tw:rounded tw:p-0 tw:outline tw:outline-gray-400 tw:hover:outline-transparent"
            :color="props.color || '#FF0000'"
            v-bind="vProps">
          </v-btn>
        </div>
      </template>
      <div class="tw:relative tw:flex-col tw:gap-3 tw:overflow-x-hidden tw:bg-tower-alt tw:p-3">
        <div name="customColorMenu" :class="showColorPickerMenu && 'tw:hidden'">
          <div>
            <div class="tw:mb-3 tw:flex tw:justify-between tw:gap-2">
              <div class="tw:flex tw:items-center tw:px-2 tw:text-xs"><span>Custom colors</span></div>

              <button
                name="removeColorsButton"
                class="tw:m-0.5 tw:flex tw:h-6 tw:items-center tw:justify-center tw:rounded tw:p-1 tw:text-xs tw:shadow-inner tw:outline tw:outline-tower-border tw:ease-in tw:hover:bg-gray-600/10"
                @click="toggleEditCustomColors">
                <Check v-if="editMode" :size="16" class="tw:text-green-800" />
                <Edit2 v-else :size="16" />
              </button>
            </div>

            <div
              name="customSwatches"
              class="tw:flex tw:flex-wrap tw:items-center tw:gap-x-2 tw:gap-y-1 tw:px-2.5 tw:py-0.5"
              :class="editMode ? 'tw:gap-x-2' : 'tw:gap-x-1'">
              <ColorSwatch
                v-for="(swatch, index) in customColors"
                :selected="swatch.selected"
                @click="handleSwatchClick(swatch)"
                @remove="handleSwatchRemove(swatch)"
                :color="swatch.color"
                :key="`${swatch.color}-${index}`"
                :editable="editMode && !swatch.selected" />

              <!-- add custom color button -->
              <button
                name="addCustomColorButton"
                class="tw:m-0.5 tw:flex tw:h-[28px] tw:w-[28px] tw:items-center tw:justify-center tw:rounded tw:shadow-inner tw:outline tw:outline-tower-border tw:ease-in tw:hover:bg-gray-600/10"
                @click="handleAddCustomColor">
                <Plus :size="16" />
              </button>
            </div>
          </div>
        </div>

        <div
          :class="!showColorPickerMenu && 'tw:hidden tw:border tw:border-solid tw:border-tower-border tw:bg-tower-alt'">
          <v-color-picker
            class="tw:border tw:border-solid tw:border-tower-border tw:p-0"
            dot-size="20"
            compact
            mode="hex"
            :modes="['hex']"
            width="172px"
            canvas-height="100px"
            v-model="colorPickerSelection" />
          <div class="tw:flex tw:justify-around tw:border tw:border-solid tw:border-tower-border tw:p-2">
            <v-btn variant="flat" size="small" @click="handleCancel" name="cancelAction"> CANCEL </v-btn>
            <v-btn
              :disabled="colorSwatches.some((s: ColorSwatchItem) => s.color === colorPickerSelection)"
              variant="flat"
              size="small"
              color="primary"
              @click="handleSave"
              name="confirmAction">
              SAVE
            </v-btn>
          </div>
        </div>
      </div>
    </v-menu>
  </div>
</template>

<style></style>
